import React, { useState } from "react";
import {
    Box,
    Button,
    Typography,
    Stepper,
    Step,
    StepLabel,
    TextField,
    FormControlLabel,
    Checkbox,
    Radio,
    RadioGroup,
    IconButton,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import SensorSelection from "./SensorSelection";
import DetectionSelection from "./DetectionSelection";

const EntityForm = ({ onSave, onCancel }) => {
    const [activeStep, setActiveStep] = useState(0); // Track the current step
    const [entityName, setEntityName] = useState("");
    const [isGeoLocated, setIsGeoLocated] = useState(false);
    const [geoType, setGeoType] = useState(""); // "static" or "dynamic"
    const [keys, setKeys] = useState([{ key: "" }]); // Dynamic keys
    const [selectedSensors, setSelectedSensors] = useState([]);
    const [detections, setDetections] = useState([]);

    const isEntityNameValid = entityName.trim() !== "";
    const isSensorStepComplete = selectedSensors.length > 0;
    const isDetectionStepComplete = detections.length > 0;

    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleSave = () => {
        const entityData = {
            name: entityName,
            isGeoLocated,
            geoType,
            keys: keys.map((item) => item.key).filter((key) => key.trim() !== ""),
            sensors: selectedSensors,
            detections,
        };
        onSave(entityData);
    };

    const handleAddKey = () => {
        setKeys((prevKeys) => [...prevKeys, { key: "" }]);
    };

    const handleRemoveKey = (index) => {
        setKeys((prevKeys) => prevKeys.filter((_, i) => i !== index));
    };

    const handleKeyChange = (index, value) => {
        setKeys((prevKeys) =>
            prevKeys.map((item, i) => (i === index ? { key: value } : item))
        );
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                maxWidth: 1000,
                width: "100%",
                mx: "auto",
                mt: 4,
                p: 3,
                boxShadow: 3,
                borderRadius: 2,
                bgcolor: "background.paper",
            }}
        >
            <Typography variant="h6" gutterBottom>
                Create a New Entity
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, textAlign: "center" }}>
                An entity is a blueprint for what needs to be created. Define its name, optional geo-location, and custom keys to structure it.An Entity represents a unit to monitor or manage in various scenarios. For logistics, it might be a Vehicle tracking routes. In traffic systems, it’s a Traffic Intersection monitoring congestion. For security, it’s a Home ensuring safety. Each Entity groups sensors and detections for specific tasks.
            </Typography>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                <Step>
                    <StepLabel>Entity Details</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Select Sensors</StepLabel>
                </Step>
                <Step>
                    <StepLabel>Select Detections</StepLabel>
                </Step>
            </Stepper>

            {activeStep === 0 && (
                <Box sx={{ width: "100%" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Entity Details
                    </Typography>
                    <TextField
                        label="Entity Name"
                        fullWidth
                        value={entityName}
                        onChange={(e) => setEntityName(e.target.value)}
                        helperText="Example: Vehicle, Traffic Intersection, or Weather Station"
                        sx={{ mb: 3 }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isGeoLocated}
                                onChange={(e) => setIsGeoLocated(e.target.checked)}
                            />
                        }
                        label="Is this entity geo-located?"
                        sx={{ mb: 2 }}
                    />
                    {isGeoLocated && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Select Geo-Location Type:
                            </Typography>
                            <RadioGroup
                                value={geoType}
                                onChange={(e) => setGeoType(e.target.value)}
                                row
                            >
                                <FormControlLabel
                                    value="static"
                                    control={<Radio />}
                                    label="Static (Fixed Location)"
                                />
                                <FormControlLabel
                                    value="dynamic"
                                    control={<Radio />}
                                    label="Dynamic (Requires GPS)"
                                />
                            </RadioGroup>
                            {geoType === "static" && (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                    Latitude and Longitude must be provided when creating the entity.
                                </Typography>
                            )}
                            {geoType === "dynamic" && (
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                    A GPS sensor must be attached to this entity for dynamic tracking.
                                </Typography>
                            )}
                        </Box>
                    )}
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        Custom Keys
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Add custom fields (keys) to uniquely identify or structure this entity. Keys can include identifiers, addresses, etc.
                    </Typography>
                    {keys.map((item, index) => (
                        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }} key={index}>
                            <TextField
                                label={`Key ${index + 1}`}
                                fullWidth
                                value={item.key}
                                onChange={(e) => handleKeyChange(index, e.target.value)}
                            />
                            <IconButton onClick={() => handleRemoveKey(index)}>
                                <Delete />
                            </IconButton>
                        </Box>
                    ))}
                    <Button
                        startIcon={<Add />}
                        variant="outlined"
                        onClick={handleAddKey}
                        sx={{ mb: 3 }}
                    >
                        Add Key
                    </Button>
                    <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!isEntityNameValid}
                        >
                            Next
                        </Button>
                    </Box>
                </Box>
            )}

            {activeStep === 1 && (
                <Box sx={{ width: "100%" }}>
                    <SensorSelection
                        selectedSensors={selectedSensors}
                        setSelectedSensors={setSelectedSensors}
                    />
                    <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
                        <Button variant="outlined" onClick={handleBack}>
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!isSensorStepComplete}
                        >
                            Next
                        </Button>
                    </Box>
                </Box>
            )}

            {activeStep === 2 && (
                <Box sx={{ width: "100%" }}>
                    <DetectionSelection
                        selectedSensors={selectedSensors}
                        setDetections={setDetections}
                    />
                    <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
                        <Button variant="outlined" onClick={handleBack}>
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={!isDetectionStepComplete}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default EntityForm;
